import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Download, Share2, Building2, Target, Users, TrendingUp, CheckCircle, AlertTriangle, Loader2, Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

export default function EnterpriseAnalysisPage() {
  const navigate = useNavigate();
  const { sessionId } = useParams();
  const [analysis, setAnalysis] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    const fetchAnalysis = async () => {
      try {
        const response = await axios.get(`${API}/api/enterprise/analysis/${sessionId}`);
        setAnalysis(response.data);
      } catch (err) {
        setError(err.response?.data?.detail || "Failed to load analysis");
      } finally {
        setIsLoading(false);
      }
    };

    fetchAnalysis();
  }, [sessionId]);

  const handleCopySection = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  const handleDownloadPdf = async () => {
    setIsDownloading(true);
    try {
      const response = await axios.get(`${API}/api/enterprise/analysis/${sessionId}/pdf`, {
        responseType: "blob"
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `decisora-enterprise-${sessionId.slice(0, 8)}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch {
      toast.error("Failed to download PDF");
    } finally {
      setIsDownloading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto" />
          <p className="text-sm text-muted-foreground">Generating enterprise analysis...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="text-center space-y-4">
          <AlertTriangle className="h-8 w-8 text-destructive mx-auto" />
          <p className="text-sm text-muted-foreground">{error}</p>
          <Button variant="outline" onClick={() => navigate("/")}>
            Back to Home
          </Button>
        </div>
      </div>
    );
  }

  const result = analysis?.result || {};

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-background/60 backdrop-blur-xl border-b border-border/50">
        <div className="max-w-4xl mx-auto px-4 md:px-6 h-14 md:h-16 flex items-center justify-between">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </button>
          <span className="text-xs px-2 py-1 rounded-full bg-primary/20 text-primary">
            Enterprise Analysis
          </span>
        </div>
      </header>

      <div className="pt-24 pb-12 px-4 md:px-6">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            {/* Decision Context */}
            <div className="floating-card p-5">
              <div className="flex items-start gap-3">
                <Building2 className="h-5 w-5 text-primary mt-0.5" />
                <div className="flex-1">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Decision</p>
                  <p className="text-sm text-foreground">{analysis?.decision_text}</p>
                  {analysis?.priority && (
                    <div className="flex items-center gap-2 mt-2">
                      <Target className="h-3 w-3 text-muted-foreground" />
                      <span className="text-xs text-muted-foreground">
                        Priority: <span className="text-primary capitalize">{analysis.priority}</span>
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Quick Summary Block */}
            {result.executive_summary && (
              <div className="p-4 rounded-xl border border-border/60 bg-card/50" data-testid="enterprise-quick-summary">
                <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
                  <span className="text-muted-foreground">
                    <span className="mr-1.5">🔍</span>Recommended: <span className="text-foreground font-medium">{result.executive_summary.split('.')[0]}</span>
                  </span>
                  <span className="text-muted-foreground">
                    <span className="mr-1.5">📊</span>Confidence: <span className="text-foreground font-medium">{result.decision_comparison?.length >= 2 ? "High" : "Moderate"}</span>
                  </span>
                  {analysis?.priority && (
                    <span className="text-muted-foreground">
                      <span className="mr-1.5">⚠️</span>Risk: <span className="text-foreground font-medium capitalize">{analysis.priority === "risk" ? "Low (risk-minimized)" : analysis.priority === "speed" ? "Medium" : "Moderate"}</span>
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Section 1: Executive Summary */}
            <Section
              icon={<CheckCircle className="h-4 w-4 text-primary" />}
              title="Executive Summary"
              content={result.executive_summary}
              onCopy={() => handleCopySection(result.executive_summary)}
            />

            {/* Section 2: Logic Tree / Breakdown */}
            <Section
              icon={<TrendingUp className="h-4 w-4 text-primary" />}
              title="Logic Tree & Dependencies"
            >
              {result.logic_tree && (
                <div className="space-y-4">
                  {result.logic_tree.department_dependencies?.length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">Department Dependencies</p>
                      <ul className="space-y-1">
                        {result.logic_tree.department_dependencies.map((item, i) => (
                          <li key={i} className="text-sm text-foreground flex items-start gap-2">
                            <span className="text-primary mt-1">•</span>
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {result.logic_tree.financial_impact && (
                    <div>
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">Financial Impact</p>
                      <p className="text-sm text-foreground">{result.logic_tree.financial_impact}</p>
                    </div>
                  )}
                  {result.logic_tree.strategic_impact && (
                    <div>
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">Strategic Impact</p>
                      <p className="text-sm text-foreground">{result.logic_tree.strategic_impact}</p>
                    </div>
                  )}
                </div>
              )}
            </Section>

            {/* Section 3: Decision Comparison */}
            <Section
              icon={<Target className="h-4 w-4 text-primary" />}
              title="Decision Comparison"
            >
              {result.decision_comparison && (
                <div className="space-y-4">
                  {result.decision_comparison.map((option, i) => (
                    <div key={i} className="p-3 bg-secondary/30 rounded-lg">
                      <p className="text-sm font-medium text-foreground mb-2">{option.option}</p>
                      <div className="grid md:grid-cols-2 gap-3 text-xs">
                        <div>
                          <p className="text-muted-foreground mb-1">Outcomes</p>
                          <p className="text-foreground">{option.outcome}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground mb-1">Risk Level</p>
                          <p className="text-foreground">{option.risk}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Section>

            {/* Section 4: Technical Steps */}
            <Section
              icon={<Building2 className="h-4 w-4 text-primary" />}
              title="Technical Implementation"
            >
              {result.technical_steps?.length > 0 && (
                <ol className="space-y-2">
                  {result.technical_steps.map((step, i) => (
                    <li key={i} className="text-sm text-foreground flex items-start gap-3">
                      <span className="text-primary font-medium w-5 flex-shrink-0">{i + 1}.</span>
                      {step}
                    </li>
                  ))}
                </ol>
              )}
            </Section>

            {/* Section 5: Operational View */}
            <Section
              icon={<Users className="h-4 w-4 text-primary" />}
              title="Operational View"
              content={result.operational_view}
            />

            {/* Section 6: Strategic Next Step */}
            <div className="glow-card p-5">
              <div className="flex items-center gap-2 mb-3">
                <CheckCircle className="h-4 w-4 text-primary" />
                <p className="text-xs font-medium text-primary uppercase tracking-wider">Strategic Next Step</p>
              </div>
              <p className="text-sm text-foreground font-medium">{result.strategic_next_step}</p>
            </div>

            {/* API Engine Marketing Section */}
            <div className="pt-2">
              <div className="h-px w-full bg-border/40 mb-6" />
              <div className="text-center space-y-3 px-4" data-testid="api-engine-section">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Decisora API Engine Ready for Enterprise Integration
                </p>
                <p className="text-sm text-muted-foreground max-w-md mx-auto leading-relaxed">
                  Connect your company&apos;s internal tools, workflows, CRMs, dashboards, or applications directly with the Decisora Decision Engine through secure API access.
                </p>
                <Button
                  onClick={() => navigate("/contact")}
                  className="h-9 px-6 rounded-full text-sm bg-primary hover:bg-primary/90"
                  data-testid="request-api-access-btn"
                >
                  Request API Access Keys
                </Button>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-center gap-3 pt-4">
              <Button
                variant="outline"
                onClick={handleDownloadPdf}
                disabled={isDownloading}
                className="h-10 px-6 rounded-full text-sm"
                data-testid="download-pdf-btn"
              >
                {isDownloading ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Download className="h-4 w-4 mr-2" />
                )}
                Download PDF
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate("/enterprise")}
                className="h-10 px-6 rounded-full text-sm"
              >
                New Analysis
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate("/")}
                className="h-10 px-6 rounded-full text-sm"
              >
                Back to Home
              </Button>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}

function Section({ icon, title, content, children, onCopy }) {
  return (
    <div className="floating-card p-5">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          {icon}
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{title}</p>
        </div>
        {onCopy && (
          <button
            onClick={onCopy}
            className="text-muted-foreground hover:text-foreground p-1"
          >
            <Copy className="h-3 w-3" />
          </button>
        )}
      </div>
      {content && <p className="text-sm text-foreground leading-relaxed">{content}</p>}
      {children}
    </div>
  );
}
